import { Button } from "@/components/ui/button.tsx"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import {
  findIntegrationByName,
  getIntegrations,
  IntegrationWithJobInfo,
  upsertIntegration,
  updateIntegration,
} from "@/api/integration"

const BOL_EMAIL_SYNC_NAME = "bol-email-sync"
const STABLE_USERNAME = "default"

interface ConnectBolEmailSyncPageProps {
  redirectTo?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectBolEmailSyncPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const currentPath = window.location.pathname

  const [emailIntegrations, setEmailIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [bolIntegrationId, setBolIntegrationId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (!res.ok) {
      setLoadError("Could not load integrations")
      return
    }
    const all = res.value.data
    setEmailIntegrations(all.filter((i: IntegrationWithJobInfo) => i.name === "gmail" || i.name === "microsoft"))
    const bol = findIntegrationByName(all, BOL_EMAIL_SYNC_NAME)
    if (bol) {
      setBolIntegrationId(bol.id)
    }
  }, [])

  async function handleSave() {
    setLoading(true)
    setLoadError(null)
    let id = bolIntegrationId
    if (!id) {
      const created = await upsertIntegration(BOL_EMAIL_SYNC_NAME, STABLE_USERNAME, {})
      if (!created.ok) {
        setLoadError("Could not enable BOL email sync")
        setLoading(false)
        return
      }
      id = created.value.data.id
      setBolIntegrationId(id)
    }
    const dataRes = await updateIntegration(id, {
      data: {},
      enabled: true,
    })
    if (!dataRes.ok) {
      setLoadError("Could not update settings")
      setLoading(false)
      return
    }
    setLoading(false)
    setLocation(redirectTo)
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md mx-auto">
        <div class="flex items-center justify-center gap-3">
          <img src={gmailLogo} alt="Gmail" class="h-10 w-10 object-contain" />
          <img src={microsoftLogo} alt="Microsoft 365" class="h-10 w-10 object-contain" />
        </div>
        <h1 class="text-xl font-semibold text-gray-900 text-center">Sync BOLs via email</h1>
        <p class="text-gray-500 text-sm text-center">
          We scan PDF attachments on synced email threads from <strong>all</strong> Gmail and Microsoft mailboxes
          connected for your organization, and match them to deductions using purchase order numbers. Connect additional
          mailboxes below if needed.
        </p>

        <div class="space-y-3">
          <p class="text-sm font-medium text-gray-800">Connect email</p>
          <ConnectMailboxButtons
            gmailLabel="Add Gmail"
            microsoftLabel="Add Microsoft 365"
          />
        </div>

        {emailIntegrations.length > 0 && (
          <div class="border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 class="font-medium text-gray-900 text-sm">Connected mailboxes</h3>
            {emailIntegrations.map((e: IntegrationWithJobInfo) => {
              let updated = ""
              if (e.session_updated_at) {
                const d = DateTime.fromISO(e.session_updated_at)
                updated = d.toLocaleString(DateTime.DATETIME_MED)
              }
              return (
                <div key={e.id} class="flex justify-between gap-4 text-sm">
                  <span class="text-gray-700 truncate">
                    {e.name}: {e.username}
                  </span>
                  {updated ? <span class="text-gray-400 shrink-0">{updated}</span> : null}
                </div>
              )
            })}
          </div>
        )}

        {loadError ? <p class="text-sm text-red-600">{loadError}</p> : null}

        <div class="flex justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)}>
            Back
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={loading} class="bg-gray-900 text-white">
            {loading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </CenterColumnContainer>
  )
}
